import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import FacilityAccessDisplay from '../components/FacilityAccessDisplay';
import PlanDetailsModal from '../components/PlanDetailsModal';
import NextPlanPurchase from '../components/NextPlanPurchase';

const PublicPlansPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters for member type filtering
      const queryParams = new URLSearchParams();
      
      // Use member type filtering if user is logged in and has a member type
      if (user && user.memberType) {
        const memberTypeId = user.memberType._id || user.memberType;
        queryParams.append('memberType', memberTypeId);
      }
      
      const queryString = queryParams.toString();
      const url = `/plans${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      setPlans(response.data.data);
      setError('');
    } catch (err) {
      setError(t('publicPlans:errors.fetchFailed'));
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    fetchPlans();
    if (user) {
      fetchMembershipInfo();
    } else {
      setMembershipInfo(null);
    }
  }, [fetchPlans, user]);

  const formatPrice = (plan) => {
    // If plan has calculated price, use that (member type specific)
    if (plan.calculatedPrice !== undefined) {
      return `₺${plan.calculatedPrice.toFixed(2)}`;
    }
    // Otherwise use the base price
    return `₺${plan.price.toFixed(2)}`;
  };

  const getDiscountInfo = (plan) => {
    if (plan.calculatedPrice !== undefined && plan.price > plan.calculatedPrice) {
      const discountAmount = plan.price - plan.calculatedPrice;
      const discountPercentage = ((discountAmount / plan.price) * 100).toFixed(0);
      return {
        hasDiscount: true,
        originalPrice: plan.price,
        discountAmount,
        discountPercentage
      };
    }
    return { hasDiscount: false };
  };

  const formatDuration = (duration) => {
    if (duration === 1) return t('publicPlans:duration.oneDay');
    if (duration < 30) return t('publicPlans:duration.days', { count: duration });
    if (duration === 30) return t('publicPlans:duration.oneMonth');
    if (duration < 365) return t('publicPlans:duration.months', { count: Math.round(duration / 30) });
    return t('publicPlans:duration.years', { count: Math.round(duration / 365) });
  };

  const fetchMembershipInfo = async () => {
    try {
      const response = await api.get('/users/membership-info');
      setMembershipInfo(response.data.data);
    } catch (err) {
      console.error('Error fetching membership info:', err);
      setMembershipInfo(null);
    }
  };

  const handleSelectPlan = (plan) => {
    // Check if user has an active membership and can purchase next plan
    if (user && membershipInfo?.hasMembership) {
      // User has active membership - this should trigger next plan purchase
      alert(t('publicPlans:alerts.nextPlanAvailable', { planName: plan.name }));
    } else {
      // Regular plan selection flow
      console.log('Selected plan:', plan);
      alert(t('publicPlans:alerts.planSelected', { planName: plan.name }));
    }
  };

  const handleViewDetails = (plan) => {
    setSelectedPlan(plan);
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setSelectedPlan(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {t('publicPlans:title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('publicPlans:subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Next Plan Purchase Section - Only show for users with active membership */}
      {user && membershipInfo && membershipInfo.hasMembership && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
            {t('publicPlans:nextPlan.title')}
          </Typography>
          <NextPlanPurchase />
        </Box>
      )}

      {/* Current Membership Info */}
      {user && membershipInfo && membershipInfo.hasMembership && (
        <Box sx={{ mb: 4 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('publicPlans:currentMembership.title')}
            </Typography>
            <Typography variant="body2">
              {t('publicPlans:currentMembership.planName')}: <strong>{membershipInfo.plan?.name}</strong>
            </Typography>
            <Typography variant="body2">
              {t('publicPlans:currentMembership.expiresOn')}: <strong>{membershipInfo.endDate ? new Date(membershipInfo.endDate).toLocaleDateString() : 'N/A'}</strong>
            </Typography>
            {membershipInfo.status === 'expiring_soon' && (
              <Typography variant="body2" color="warning.main">
                ⚠️ {t('publicPlans:currentMembership.expiringSoon')}
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {plans.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t('publicPlans:empty.noPlans')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {plans.map((plan, index) => (
            <Grid size={{xs:12 , md:6 , lg:4}} key={plan._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8]
                  },
                  // Highlight middle plan if there are 3 plans
                  ...(plans.length === 3 && index === 1 && {
                    border: '2px solid',
                    borderColor: 'primary.main',
                    transform: 'scale(1.05)',
                    '&:hover': {
                      transform: 'scale(1.05) translateY(-4px)'
                    }
                  })
                }}
              >
                {plans.length === 3 && index === 1 && (
                  <Chip
                    label={t('publicPlans:badges.mostPopular')}
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1
                    }}
                  />
                )}
                
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h5" component="h2" gutterBottom align="center">
                    {plan.name}
                  </Typography>
                  
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {(() => {
                      const discountInfo = getDiscountInfo(plan);
                      return (
                        <>
                          {discountInfo.hasDiscount && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ textDecoration: 'line-through' }}
                              >
                                ₺{discountInfo.originalPrice.toFixed(2)}
                              </Typography>
                              <Chip 
                                label={t('publicPlans:discount.label', { percentage: discountInfo.discountPercentage })}
                                color="success"
                                size="small"
                              />
                            </Box>
                          )}
                          <Typography variant="h3" component="div" color="primary">
                            {formatPrice(plan)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('publicPlans:pricing.per', { duration: formatDuration(plan.duration) })}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.description}
                  </Typography>

                  <Typography variant="h6" gutterBottom>
                    {t('publicPlans:features.title')}
                  </Typography>
                  <List dense>
                    {(() => {
                      // Use allFeatures if available (member type specific), otherwise use base features
                      const featuresToShow = plan.allFeatures || plan.features;
                      
                      return featuresToShow.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ));
                    })()}
                  </List>

                  {/* Facility Access Display */}
                  {plan.facilityAccess && plan.facilityAccess.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <FacilityAccessDisplay 
                        facilityAccess={plan.facilityAccess}
                        compact={true}
                      />
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 3, pt: 0, flexDirection: 'column', gap: 1, '& .MuiButton-root': { margin: 0 } }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => handleViewDetails(plan)}
                    sx={{ margin: 0 }}
                  >
                    {t('publicPlans:actions.viewDetails')}
                  </Button>
                  <Button
                    fullWidth
                    variant={plans.length === 3 && index === 1 ? "contained" : "outlined"}
                    size="large"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={!user || (user?.membershipPlan === plan._id)}
                    sx={{ margin: 0 }}
                  >
                    {!user ? t('publicPlans:actions.loginToSelect') : 
                     user?.membershipPlan === plan._id ? t('publicPlans:actions.currentPlan') : 
                     (membershipInfo?.hasMembership ? t('publicPlans:actions.selectAsNext') : t('publicPlans:actions.selectPlan'))}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!user && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity="info">
            {t('publicPlans:loginPrompt')}
          </Alert>
        </Box>
      )}

      {/* Plan Details Modal */}
      <PlanDetailsModal
        plan={selectedPlan}
        open={detailsModalOpen}
        onClose={handleCloseDetails}
        onSelect={handleSelectPlan}
        currentUserPlan={user?.membershipPlan}
        isLoggedIn={!!user}
      />
    </Container>
  );
};

export default PublicPlansPage;